import { Injectable, OnDestroy, EventEmitter } from '@angular/core';
import { PdfInstanceManagerService } from '../instance/pdf-instance-manager.service';
import { TabService } from '../tab.service';
import { BehaviorSubject, Observable, Subscription } from 'rxjs';
import { SettingsService } from '../settings/settings.service';
import {
  PdfToolType,
  PdfStrokeColor
} from 'src/app/core/pdf-utils/enums';
import { AnnotationEvent } from 'src/models/Events';
import { IToolStyles } from 'src/app/core/pdf-utils/interfaces';
import { ISettings } from 'src/app/core/interfaces';
import { CurrentUiStoreService } from '../ui/current-ui-store.service';
import { AsgardFeatureNames } from 'src/models/AsgardFeatures';
import { CurrentUIState } from 'src/models/UIState';
import { DocumentToolsService, ToolEventSource } from './document-tools.service';
import { resetCursorIcon } from 'src/app/core/common';
import { createToolStylesFromShapeStyles } from 'src/app/core/pdf-utils/drawing-utils';
import { Core } from '@pdftron/webviewer';


@Injectable({
  providedIn: 'root'
})
export class DocumentDrawingToolsService implements OnDestroy {
  private readonly subscriptions: Subscription = new Subscription();

  private drawModeActiveSubject: BehaviorSubject<boolean>;
  // private shapeStyles: PdfShapeStyles = {};
  private shapeStyles: IToolStyles = {};
  private shapes: PdfToolType[] = [
    PdfToolType.Rectangle,
    PdfToolType.Ellipse,
    PdfToolType.Arrow,
    PdfToolType.Line,
    PdfToolType.FreeHand
  ];
  private defaultStyles: IToolStyles = {
    strokeColor: PdfStrokeColor.Black,
    strokeThickness: 1
  };
  private shapeStylesSubject: BehaviorSubject<IToolStyles>;
  private selectedShapesSubject: BehaviorSubject<Core.Annotations.MarkupAnnotation[]>;
  private drawingsDefaultVisibilitySub: BehaviorSubject<boolean>;

  /* Indicates whether user has interacted with drawing tools. If set to 'true' changing default drawing styles
     on Settings page will not affect current shape styles
  */
  private interactedWithDrawingTools: boolean = false;
  private pendingFreehandAnnotation: Core.Annotations.FreeHandAnnotation | null = null;

  private get docViewer(): Core.DocumentViewer | null {
    return this.tabService.activeTab && this.tabService.activeTab.id
      ? this.pdfInstanceManager.getDocViewer(this.tabService.activeTab.id)
      : null;
  }

  private get Annotations(): any | null {
    if (this.tabService.activeTab) {
      const instance = this.pdfInstanceManager.getViewerInstance(this.tabService.activeTab.id);
      if (instance) {
        return instance.Core.Annotations;
      }
    }
    return null;
  }

  get drawModeActiveObservable(): Observable<boolean> {
    return this.drawModeActiveSubject.asObservable();
  }

  constructor(
    private readonly pdfInstanceManager: PdfInstanceManagerService,
    private readonly docToolsService: DocumentToolsService,
    private readonly tabService: TabService,
    private readonly settingsService: SettingsService,
    private uiService: CurrentUiStoreService,
  ) {
    this.shapeStylesSubject = new BehaviorSubject(this.shapeStyles);
    this.selectedShapesSubject = new BehaviorSubject([]);
    this.drawingsDefaultVisibilitySub = new BehaviorSubject(true);
    this.drawModeActiveSubject = new BehaviorSubject(false);
    this.initShapeStyles();
    this.initSubscriptions();
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  getShapeStylesSubject(): Observable<IToolStyles> {
    return this.shapeStylesSubject.asObservable();
  }

  getDrawingsDefaultVisibilitySubject(): Observable<boolean> {
    return this.drawingsDefaultVisibilitySub.asObservable();
  }

  getSelectedShapesSubject(): Observable<Core.Annotations.MarkupAnnotation[]> {
    return this.selectedShapesSubject.asObservable();
  }

  enterDrawMode(shape: PdfToolType): void {
    this.interactedWithDrawingTools = true;
    this.uiService.setItemActive(AsgardFeatureNames.DRAWING);
    if (this.tabService.activeTab) {
      resetCursorIcon(this.tabService.activeTab.id);
    }
    const toolStyles = createToolStylesFromShapeStyles(this.shapeStyles);
    this.docToolsService.enableTool(ToolEventSource.Drawing, shape, toolStyles);
    this.drawModeActiveSubject.next(true);
  }

  exitDrawMode(): void {
    if (!this.drawModeActiveSubject.value) {
      return;
    }

    this.initShapeStyles();

    if (this.docToolsService.isToolEnabled(ToolEventSource.Drawing)) {
      this.docToolsService.disableTool(ToolEventSource.Drawing);
    }

    this.drawModeActiveSubject.next(false);
  }

  updateShapeStyles(shape: PdfToolType, styles: Partial<IToolStyles>): void {
    this.interactedWithDrawingTools = true;
    this._updateShapeStyles(shape, styles);
  }

  updateAnnotationStyles(
    annotation: Core.Annotations.MarkupAnnotation,
    styles: Partial<IToolStyles>
  ): void {
    if (this.docViewer) {
      const annotationManager = this.docViewer.getAnnotationManager();
      if (styles.strokeColor) {
        // AnnotationManager#setAnnotationStyles does not update colors properties for some reason
        annotationManager.exportAnnotations({
          annotList: [annotation],
          widgets: false,
          links: false,
          fields: false
        }).then(annotXml => {
          const newXml = annotXml.replace(
            /color=\"(#{1}[0-9A-F]{6})\"/g,
            `color="${styles.strokeColor}"`
          );
          annotationManager.importAnnotations(newXml);
        });
      }
      if (styles.strokeThickness) {
        annotationManager.setAnnotationStyles(annotation, oldStyles => ({
          ...oldStyles,
          StrokeThickness: styles.strokeThickness
        }));
      }
    }
  }

  hideAnnotations(): void {
    if (this.docViewer) {
      const annotationsManager = this.docViewer.getAnnotationManager();
      annotationsManager.hideAnnotations(
        this.getDrawingAnnotationsFromList(
          annotationsManager.getAnnotationsList()
        )
      );
    }
  }

  showAnnotations(): void {
    if (this.docViewer) {
      const annotationsManager = this.docViewer.getAnnotationManager();
      annotationsManager.showAnnotations(
        this.getDrawingAnnotationsFromList(
          annotationsManager.getAnnotationsList()
        )
      );
    }
  }

  private initSubscriptions(): void {
    this.subscriptions.add(
      this.settingsService
        .getSettingsObservable()
        .subscribe((settings: ISettings) => {
          this.defaultStyles = settings.drawingStyles;

          if (!this.interactedWithDrawingTools) {
            this.initShapeStyles();
          }
          this.updateDrawingsDefaultVisibility(settings.drawingsVisible);
        })
    );
    this.registerAnnotationEvents();

    this.uiService.getUIState().subscribe((e: CurrentUIState) => {
      if (!e[AsgardFeatureNames.DRAWING].active && this.drawModeActiveSubject.value) {
        this.exitDrawMode();
      }
    });
  }

  private registerAnnotationEvents(): void {
    this.registerAnnotationEvent(
      this.pdfInstanceManager.annotationsLoaded,
      () => {
        if (!this.drawingsDefaultVisibilitySub.getValue()) {
          this.hideAnnotations();
        }
      }
    );

    this.registerAnnotationEvent(
      this.pdfInstanceManager.freeHandAnnotationCreated,
      (e: AnnotationEvent<Core.Annotations.FreeHandAnnotation>) => {
        if (e && e.annotations && e.annotations.length !== 0) {
          this.pendingFreehandAnnotation = e.annotations[0];
        }
      }
    );

    this.registerAnnotationEvent(
      this.pdfInstanceManager.freeHandAnnotationAdded,
      (e: AnnotationEvent<Core.Annotations.FreeHandAnnotation>) => {
        if (e && e.annotations && e.annotations.length !== 0) {
          const annotation = e.annotations[0];
          if (
            this.pendingFreehandAnnotation &&
            annotation.Id === this.pendingFreehandAnnotation.Id
          ) {
            this.pendingFreehandAnnotation = null;
          }
        }
      }
    );

    /* Preventing drawing dots when freehand drawing mode is active.
       PDFTron always adds dot in this case and does not remove it if user clicked somewhere
        before 'FreeHandCreateTool' delay interval expired.
       So, we remove the dot manually (need to add dot to AnnotationManager first
        because otherwise it starts tracking it only after delay)
    */
    this.registerAnnotationEvent(
      this.pdfInstanceManager.annotationSelected,
      (e: AnnotationEvent<Core.Annotations.Annotation>) => {
        if (this.pendingFreehandAnnotation && this.docViewer && e && e.annotations && e.annotations.length !== 0) {
          const annotationManager = this.docViewer.getAnnotationManager();
          annotationManager.addAnnotation(this.pendingFreehandAnnotation);
          annotationManager.hideAnnotation(this.pendingFreehandAnnotation);
          annotationManager.deleteAnnotation(
            this.pendingFreehandAnnotation,
            { imported: false }
          );
          this.pendingFreehandAnnotation = null;
          annotationManager.selectAnnotations(e.annotations);
        }
        this.handleAnnotationSelection();
      }
    );

    this.registerAnnotationEvent(
      this.pdfInstanceManager.annotationDeselected,
      () => this.handleAnnotationSelection()
    );
  }

  private handleAnnotationSelection(): void {
    if (this.docViewer) {
      const annotationManager = this.docViewer.getAnnotationManager();
      const drawingAnnotations = this.getDrawingAnnotationsFromList(
        annotationManager.getSelectedAnnotations()
      );
      this.selectedShapesSubject.next(drawingAnnotations);
    }
  }

  private registerAnnotationEvent(
    eventEmitter: EventEmitter<AnnotationEvent<Core.Annotations.Annotation>>,
    handler: (e?: AnnotationEvent<Core.Annotations.Annotation>) => void
  ): void {
    eventEmitter.subscribe(e => {
      if (this.tabService.activeTab && e.tabId === this.tabService.activeTab.id) {
        handler(e);
      }
    });
  }

  private initShapeStyles(): void {
    this.shapes.forEach(shape =>
      this._updateShapeStyles(shape, this.defaultStyles)
    );
  }

  private updateShapeStylesSubject(): void {
    this.shapeStylesSubject.next(this.shapeStyles);
  }

  private updateDrawingsDefaultVisibility(val: boolean): void {
    this.drawingsDefaultVisibilitySub.next(val);
  }

  private _updateShapeStyles(
    shape: PdfToolType,
    styles: Partial<IToolStyles>
  ): void {
    this.shapeStyles = { ...this.shapeStyles, ...styles };
    this.updateShapeStylesSubject();
    const toolStyles = createToolStylesFromShapeStyles(this.shapeStyles);
    if(this.tabService.activeTab && this.tabService.activeTab.id){
      this.docToolsService.setToolStyles(this.tabService.activeTab.id, shape, toolStyles);
    }
  }

  private isDrawingAnnotation(annotation: Core.Annotations.Annotation): boolean {
    if (!this.Annotations) {
      return false;
    }

    const drawingAnnotationTypes = [
      this.Annotations.RectangleAnnotation,
      this.Annotations.EllipseAnnotation,
      this.Annotations.LineAnnotation,
      this.Annotations.FreeHandAnnotation
    ];
    return !!drawingAnnotationTypes.find(aType => annotation instanceof aType);
  }

  private getDrawingAnnotationsFromList(
    annotations: Core.Annotations.Annotation[] = []
  ): Core.Annotations.MarkupAnnotation[] {
    return annotations.filter(a =>
      this.isDrawingAnnotation(a)
    ) as Core.Annotations.MarkupAnnotation[];
  }
}
